import React from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";

/**
 * Last-line-of-defense error boundary. React errors that don't get caught by
 * the api interceptor or a component's own try/catch land here — the user
 * sees a friendly recovery card instead of the red webpack overlay + a blank
 * app. We log the error to console so it still shows up in DevTools.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, errorInfo) {
    console.error("[ErrorBoundary]", error, errorInfo);
    // Optional: POST to backend for logging (skipped for now).
  }
  reset = () => this.setState({ hasError: false, error: null });
  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ background: "var(--paper)" }} data-testid="error-boundary">
        <div className="pm-card p-8 max-w-md text-center">
          <AlertTriangle size={40} className="mx-auto mb-4" style={{ color: "#c98a3b" }} />
          <h1 className="font-display font-bold text-2xl mb-2">Something didn't render right</h1>
          <p className="text-sm mb-5" style={{ color: "var(--text-secondary)" }}>
            We hit a snag on this page. Refresh to try again — your data is safe. If it keeps happening on the same screen, drop a note in the Feedback tab so we can fix it.
          </p>
          <div className="flex gap-3 justify-center flex-wrap">
            <button
              onClick={() => { this.reset(); window.location.reload(); }}
              className="pm-btn-primary inline-flex items-center gap-2"
              data-testid="error-boundary-reload"
            >
              <RefreshCw size={14} /> Refresh page
            </button>
            <button
              onClick={() => { this.reset(); window.location.href = "/app/dashboard"; }}
              className="pm-badge inline-flex items-center gap-2"
              style={{ background: "var(--muted)", padding: "8px 14px", cursor: "pointer" }}
              data-testid="error-boundary-dashboard"
            >
              Back to dashboard
            </button>
          </div>
          {process.env.NODE_ENV !== "production" && this.state.error && (
            <pre className="mt-4 text-xs text-left overflow-auto p-2 rounded" style={{ background: "var(--muted)", color: "var(--text-muted)", maxHeight: 200 }}>
              {String(this.state.error?.message || this.state.error)}
            </pre>
          )}
        </div>
      </div>
    );
  }
}
